/* ks-flow-bridge.js — parent side of the Keysleeves matcher flow.
 *
 * Owns the flow overlay/iframe and is the ONLY thing that talks to the
 * matcher over the network. The flow document (ks-flow.html) is served from
 * the theme/CDN and may be cross-origin, so it never fetches anything itself;
 * it asks this file to, over postMessage.
 *
 * Contract
 *   iframe -> parent  { type:'ks-flow-match-request', requestId,
 *                       image:<Blob|dataURL>, consent:true,
 *                       promotionConsent, contactConsent, brand? }
 *   parent -> iframe  { type:'ks-flow-match-response', requestId, ok, payload }
 *                       payload = raw /match JSON, or { error:'…' }
 *   iframe -> parent  { type:'ks-flow-feedback', body:{ attemptId, … } }
 *                       parent POSTs /feedback, fire-and-forget
 *   iframe -> parent  { type:'ks-flow-attribution', body:{ attemptId,
 *                       productId, variantId, confidenceLabel, source } }
 *                       parent stamps the cart attributes, fire-and-forget
 *   iframe -> parent  { type:'keysleeves-matcher-ready' | 'keysleeves-matcher-close' }
 *   parent -> iframe  { type:'keysleeves-matcher-open' }
 *
 * Configuration — either window.KS_FLOW_CONFIG before this script, or
 * data-* attributes on the script tag:
 *   flowSrc     (required) URL of ks-flow.html
 *   proxyRoot   default '/apps/keysleeves-photo-search'
 *   hash        preset hash string appended to flowSrc (no leading '#')
 *   presetUrl   JSON to fetch for the preset ({settings:{…}} or {hash:'…'})
 *   trigger     selector for open triggers, default '[data-ks-matcher-open]'
 *   brand       optional brand hint forwarded with /match
 *
 * No lab-only globals; the same file loads on the Shopify theme.
 */
(function () {
  "use strict";

  if (window.KsFlow) return;

  var script = document.currentScript;
  var dataCfg = script ? script.dataset : {};
  var cfg = window.KS_FLOW_CONFIG || {};

  function opt(name, fallback) {
    if (cfg[name] !== undefined && cfg[name] !== null && cfg[name] !== "") return cfg[name];
    if (dataCfg[name] !== undefined && dataCfg[name] !== "") return dataCfg[name];
    return fallback;
  }

  var FLOW_SRC = opt("flowSrc", "");
  var PROXY_ROOT = String(opt("proxyRoot", "/apps/keysleeves-photo-search")).replace(/\/+$/, "");
  var PRESET_URL = opt("presetUrl", "");
  var PRESET_HASH = opt("hash", "");
  var TRIGGER_SEL = opt("trigger", "[data-ks-matcher-open]");
  var BRAND = opt("brand", "");
  /* "src" (default, lab) loads flowSrc straight into the iframe.
     "srcdoc" fetches it and injects it same-origin — required on Shopify. */
  var EMBED_MODE = opt("embedMode", "src");
  var READY_TIMEOUT_MS = 10000;
  var SESSION_KEY = "ks-photo-search-session";

  /* ------------------------------------------------------------ attribution */

  /* When a customer clicks through from a matcher result to a product page we
     stamp the attempt onto the Shopify cart as cart attributes, so it rides
     along onto any order the session produces (order.note_attributes).
     Everything here is best-effort: a failed cart write must never be visible
     to the shopper, so all paths swallow their errors. The localStorage copy is
     the source of truth for the attempt + its history; every page load checks
     the cart still agrees and re-pushes if it doesn't.

     This block sits above the FLOW_SRC guard on purpose: a product page may
     load the bridge without a data-flow-src (no matcher entry point there) and
     the cart still needs the attribution to survive the navigation. */

  var ATTR_KEY = "ks-attribution";
  var ATTR_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  var ATTR_HISTORY_MAX = 5;

  function attrRead() {
    try {
      var raw = window.localStorage.getItem(ATTR_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && parsed.attemptId ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  function attrWrite(record) {
    try { window.localStorage.setItem(ATTR_KEY, JSON.stringify(record)); }
    catch (_) { /* private mode / quota — the cart write still runs */ }
  }

  /* Flatten anything id-ish (array, comma string, single id) into a list. */
  function attrIds(value) {
    var parts = Array.isArray(value) ? value : String(value == null ? "" : value).split(",");
    var out = [];
    parts.forEach(function (part) {
      var id = String(part == null ? "" : part).trim();
      if (id) out.push(id);
    });
    return out;
  }

  /* Deduped, oldest first — a repeat of an existing id moves it to the END so
     the newest touch always wins — capped to the last ATTR_HISTORY_MAX. */
  function attrMergeHistory(/* …id sources */) {
    var out = [];
    Array.prototype.slice.call(arguments).forEach(function (source) {
      attrIds(source).forEach(function (id) {
        var at = out.indexOf(id);
        if (at !== -1) out.splice(at, 1);
        out.push(id);
      });
    });
    return out.slice(-ATTR_HISTORY_MAX);
  }

  /* Build the record to persist: the previous attempt is folded into history
     first (it may never have reached the cart), then this one. */
  function attrRecord(body) {
    var previous = attrRead();
    return {
      attemptId: String(body.attemptId),
      productId: body.productId || "",
      variantId: body.variantId || "",
      confidenceLabel: body.confidenceLabel || "",
      source: body.source || "",
      ts: Date.now(),
      history: attrMergeHistory(
        previous && previous.history,
        previous && previous.attemptId,
        String(body.attemptId)
      )
    };
  }

  /* Shopify deletes a cart attribute whose value is "" — so only non-empty
     values are ever sent, and a URL-only recovery never clobbers the variant
     or confidence a richer earlier write already landed. */
  function attrPushCart(record) {
    if (!record || !record.attemptId) return Promise.resolve(false);
    try {
      return fetch("/cart.js", {
        credentials: "same-origin",
        headers: { accept: "application/json" },
        keepalive: true
      })
        .then(function (response) { return response.json(); })
        .catch(function () { return null; })
        .then(function (cart) {
          var existing = (cart && cart.attributes && cart.attributes._ks_attempt_history) || "";
          var history = attrMergeHistory(existing, record.history, record.attemptId);
          var attributes = {
            _ks_attempt_id: record.attemptId,
            /* Match time, not write time — re-asserts must not shift it. */
            _ks_matched_at: new Date(Number(record.ts) || Date.now()).toISOString(),
            _ks_attempt_history: history.join(",")
          };
          var variant = String(record.variantId || "");
          var product = String(record.productId || "");
          var confidence = String(record.confidenceLabel || "");
          if (variant) attributes._ks_matched_variant = variant;
          if (product) attributes._ks_matched_product = product;
          if (confidence) attributes._ks_confidence = confidence;

          return fetch("/cart/update.js", {
            method: "POST",
            credentials: "same-origin",
            headers: { "content-type": "application/json", accept: "application/json" },
            keepalive: true,
            body: JSON.stringify({ attributes: attributes })
          });
        })
        .then(function (response) {
          if (!response || !response.ok) throw new Error("cart update " + (response && response.status));
          return response.json();
        })
        .then(function (cart) {
          /* /cart/update.js echoes the cart — only trust the write if the
             attribute actually came back on it. */
          var landed = cart && cart.attributes && cart.attributes._ks_attempt_id === record.attemptId;
          attrConfirmed = !!landed;
          if (!landed && window.console && console.debug) {
            console.debug("ks attribution: cart did not echo the attempt");
          }
          return !!landed;
        })
        .catch(function (error) {
          if (window.console && console.debug) console.debug("ks attribution: cart write failed", error);
          return false;
        });
    } catch (error) {
      if (window.console && console.debug) console.debug("ks attribution: cart write threw", error);
      return Promise.resolve(false);
    }
  }

  function handleAttribution(body) {
    if (!body || !body.attemptId) return Promise.resolve(false);
    var record = attrRecord(body);
    attrConfirmed = false;
    attrWrite(record);
    return attrPushCart(record);
  }

  /* Last-chance re-assert: an attribute written onto a zero-item cart can be
     dropped by Shopify, and a drawer add → straight to /checkout never loads
     another theme page. Leaving the page (checkout included) fires
     visibilitychange, and keepalive lets the write outlive the navigation. */
  var attrConfirmed = false;
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState !== "hidden" || attrConfirmed) return;
    try {
      var stored = attrRead();
      if (!stored || Date.now() - (Number(stored.ts) || 0) > ATTR_MAX_AGE_MS) return;
      attrPushCart(stored);
    } catch (_) { /* never block unload */ }
  });

  /* Page load: honour a ?ks_attempt= on the URL when the postMessage never
     landed (e.g. the tab navigated before the write went out), otherwise just
     confirm the cart still carries the stored attempt and repair it if not. */
  function attrInit() {
    var param = "";
    try { param = new URL(window.location.href).searchParams.get("ks_attempt") || ""; }
    catch (_) { param = ""; }

    var stored = attrRead();
    if (stored && Date.now() - (Number(stored.ts) || 0) > ATTR_MAX_AGE_MS) stored = null;

    if (param && (!stored || stored.attemptId !== param)) {
      handleAttribution({ attemptId: param, source: "url" });
      return;
    }
    if (!stored) return;

    fetch("/cart.js", { credentials: "same-origin", headers: { accept: "application/json" } })
      .then(function (response) { return response.json(); })
      .catch(function () { return null; })
      .then(function (cart) {
        var attributes = (cart && cart.attributes) || {};
        if (attributes._ks_attempt_id === stored.attemptId && attributes._ks_attempt_history) {
          attrConfirmed = true;
          return;
        }
        attrPushCart(stored);
      })
      .catch(function (error) {
        if (window.console && console.debug) console.debug("ks attribution: cart check failed", error);
      });
  }

  try { attrInit(); } catch (_) { /* never block the bridge */ }

  if (!FLOW_SRC) return;

  /* ---------------------------------------------------------------- styles */

  var CSS = [
    "body.ks-flow-open{overflow:hidden}",
    ".ks-flow-overlay{position:fixed;z-index:2147483646;inset:0;display:grid;place-items:center;padding:24px;",
    "visibility:hidden;opacity:0;pointer-events:none;background:rgba(8,9,10,.6);",
    "-webkit-backdrop-filter:blur(9px);backdrop-filter:blur(9px);will-change:opacity;",
    "transition-property:opacity,visibility;transition-duration:180ms,0ms;transition-delay:0ms,180ms;",
    "transition-timing-function:cubic-bezier(.2,0,0,1)}",
    ".ks-flow-overlay.is-open{visibility:visible;opacity:1;pointer-events:auto;transition-delay:0ms,0ms}",
    ".ks-flow-overlay__surface{position:relative;width:min(393px,100%);height:min(852px,calc(100dvh - 48px));",
    "max-width:100%;overflow:hidden;border-radius:32px;background:transparent;",
    "box-shadow:0 28px 90px rgba(0,0,0,.56),0 0 0 1px rgba(255,255,255,.1);opacity:0;transform:translateY(8px);",
    "transition-property:opacity,transform;transition-duration:220ms;transition-timing-function:cubic-bezier(.2,0,0,1)}",
    ".ks-flow-overlay.is-open .ks-flow-overlay__surface{opacity:1;transform:translateY(0)}",
    ".ks-flow-overlay__frame{display:block;width:100%;height:100%;border:0;background:transparent;opacity:0;",
    "transition:opacity 160ms cubic-bezier(.2,0,0,1)}",
    ".ks-flow-overlay.is-frame-ready .ks-flow-overlay__frame{opacity:1}",
    ".ks-flow-overlay__close{position:absolute;z-index:10;top:12px;right:12px;width:44px;height:44px;display:grid;",
    "place-items:center;padding:0;border:0;border-radius:50%;color:rgba(255,255,255,.9);background:rgba(14,15,17,.72);",
    "box-shadow:0 8px 24px rgba(0,0,0,.28),inset 0 1px 0 rgba(255,255,255,.12);",
    "-webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);cursor:pointer;",
    "transition-property:background-color,transform;transition-duration:140ms,100ms;",
    "transition-timing-function:cubic-bezier(.2,0,0,1)}",
    ".ks-flow-overlay__close:hover{background:rgba(30,32,36,.9)}",
    ".ks-flow-overlay__close:active{transform:scale(.96)}",
    ".ks-flow-overlay__close:focus-visible{outline:2px solid #ffe8bd;outline-offset:2px}",
    ".ks-flow-overlay__close svg{width:18px;height:18px}",
    "@media (max-width:760px){.ks-flow-overlay{padding:0}",
    ".ks-flow-overlay__surface{width:100vw;height:100dvh;border-radius:0;box-shadow:none}",
    ".ks-flow-overlay__close{top:calc(10px + env(safe-area-inset-top,0px));right:calc(10px + env(safe-area-inset-right,0px))}}",
    "@media (prefers-reduced-motion:reduce){.ks-flow-overlay,.ks-flow-overlay__surface{transform:none;",
    "transition-property:opacity;transition-duration:120ms}}",
    "@media (prefers-reduced-transparency:reduce){.ks-flow-overlay{background:rgba(8,9,10,.92);",
    "-webkit-backdrop-filter:none;backdrop-filter:none}",
    ".ks-flow-overlay__close{background:#16181b;-webkit-backdrop-filter:none;backdrop-filter:none}}"
  ].join("");

  var style = document.createElement("style");
  style.setAttribute("data-ks-flow-bridge", "");
  style.textContent = CSS;
  document.head.appendChild(style);

  /* ------------------------------------------------------------------- DOM */

  var overlay = document.createElement("div");
  overlay.className = "ks-flow-overlay";
  overlay.id = "ks-flow-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Scan your key");
  overlay.setAttribute("aria-hidden", "true");
  overlay.innerHTML =
    '<div class="ks-flow-overlay__surface">' +
    '<iframe class="ks-flow-overlay__frame" title="Keysleeves key matcher" src="about:blank" allow="camera" allowtransparency="true"></iframe>' +
    '<button class="ks-flow-overlay__close" type="button" aria-label="Close key matcher">' +
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
    "</button></div>";

  function mount() {
    document.body.appendChild(overlay);
  }
  if (document.body) mount();
  else document.addEventListener("DOMContentLoaded", mount, { once: true });

  var frame = overlay.querySelector(".ks-flow-overlay__frame");
  var closeButton = overlay.querySelector(".ks-flow-overlay__close");
  var lastTrigger = null;
  var frameReady = false;
  /* Product context of the trigger that opened the flow (PDP buttons emit
     data-ks-context-product-id); lets the flow confirm-in-place instead of
     rerouting when the top match IS the page the shopper is already on. */
  var pageContext = null;

  /* ------------------------------------------------------------------- URL */

  function flowUrl(hash) {
    var url = new URL(FLOW_SRC, window.location.href);
    url.searchParams.set("embed", "1");
    url.searchParams.set("device", "1");
    url.searchParams.set("present", "1");
    if (hash) url.hash = hash;
    return url.toString();
  }

  var FRAME_ORIGIN = EMBED_MODE === "srcdoc"
    ? window.location.origin /* srcdoc inherits the embedder's origin */
    : (function () {
        try { return new URL(FLOW_SRC, window.location.href).origin; }
        catch (_) { return window.location.origin; }
      })();

  /* Preset params mirror the design-mock lane: always land on the tips screen
     in single-screen flow mode, and never inherit desktop workbench panning. */
  function presetHash(preset) {
    var params;
    if (preset && preset.settings) params = new URLSearchParams(preset.settings);
    else if (preset && preset.hash) params = new URLSearchParams(preset.hash);
    else params = new URLSearchParams(PRESET_HASH || "");
    params.set("screen", "tips-a");
    params.set("mode", "flow");
    params.set("device", "1");
    params.set("present", "1");
    params.delete("stagex");
    params.delete("stagey");
    return params.toString();
  }

  var presetPromise = PRESET_URL
    ? fetch(PRESET_URL, { cache: "no-store" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(presetHash)
        .catch(function () { return presetHash(null); })
    : Promise.resolve(presetHash(null));

  /* srcdoc mode ------------------------------------------------------------
     On a Shopify storefront neither of the obvious hosts for the flow document
     works as an iframe src:
       - assets/ks-flow.html is served by the theme CDN with
         `Content-Security-Policy: sandbox allow-scripts …` and no
         `allow-same-origin`, so the document gets an opaque origin: localStorage
         throws, the parent can't talk to it and the camera can't be delegated.
       - any storefront URL (incl. an alternate template) is served with
         `x-frame-options: DENY` / `frame-ancestors 'none'`.
     A srcdoc iframe has neither problem — it inherits the embedder's origin, so
     storage, postMessage and `allow="camera"` all behave — but it has no URL, so
     the flow's query/hash are handed over on window.__ksLoc instead. */

  var srcdocHtml = null;

  function fetchFlowHtml() {
    if (srcdocHtml) return Promise.resolve(srcdocHtml);
    return fetch(FLOW_SRC, { credentials: "omit" })
      .then(function (r) {
        if (!r.ok) throw new Error("flow " + r.status);
        return r.text();
      })
      .then(function (html) { srcdocHtml = html; return html; });
  }

  function withLocShim(html, hash) {
    var search = "?embed=1&device=1&present=1";
    try {
      var parentSpecial = new URLSearchParams(window.location.search).get("special");
      if (parentSpecial) search += "&special=" + encodeURIComponent(parentSpecial);
    } catch (_) { /* ignore */ }
    var loc = {
      search: search,
      hash: hash ? "#" + hash : ""
    };
    /* srcdoc inherits the storefront URL, so relative theme-asset paths
       (ks-sp-*.jpg) would 404 on the homepage. Point <base> at the folder
       that served ks-flow.html. */
    var assetBase = "";
    try {
      assetBase = new URL(FLOW_SRC, window.location.href).href.replace(/[^/]+$/, "");
    } catch (_) { /* leave relative */ }
    var boot = "<script>window.__ksLoc=" + JSON.stringify(loc).replace(/</g, "\\u003c") + ";<\/script>";
    if (assetBase) boot = '<base href="' + assetBase.replace(/"/g, "") + '">' + boot;
    var patched = html.replace(/<head([^>]*)>/i, function (m) { return m + boot; });
    return patched === html ? boot + html : patched;
  }

  function load() {
    return presetPromise.then(function (hash) {
      if (EMBED_MODE === "srcdoc") {
        if (frame.getAttribute("srcdoc")) return;
        return fetchFlowHtml().then(function (html) {
          if (frame.getAttribute("srcdoc")) return;
          frameReady = false;
          overlay.classList.remove("is-frame-ready");
          frame.removeAttribute("src");
          frame.setAttribute("srcdoc", withLocShim(html, hash));
        });
      }
      if (frame.src !== "about:blank" && frame.src !== "") return;
      frameReady = false;
      overlay.classList.remove("is-frame-ready");
      frame.src = flowUrl(hash);
    });
  }

  /* Don't compete with the page for bandwidth on first paint. */
  if (document.readyState === "complete") load();
  else window.addEventListener("load", function () { load(); }, { once: true });

  function whenReady() {
    if (frameReady) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        overlay.removeEventListener("ks-flow-ready", onReady);
        reject(new Error("flow did not become ready"));
      }, READY_TIMEOUT_MS);
      function onReady() { clearTimeout(timer); resolve(); }
      overlay.addEventListener("ks-flow-ready", onReady, { once: true });
    });
  }

  function toFrame(message) {
    if (!frame.contentWindow) return;
    frame.contentWindow.postMessage(message, FRAME_ORIGIN === "null" ? "*" : FRAME_ORIGIN);
  }

  /* ------------------------------------------------------------ open/close */

  function open(trigger) {
    lastTrigger = trigger || null;
    pageContext = null;
    if (trigger && trigger.dataset && trigger.dataset.ksContextProductId) {
      pageContext = {
        productId: trigger.dataset.ksContextProductId,
        title: trigger.dataset.ksContextTitle || "",
        collections: trigger.dataset.ksContextCollections || ""
      };
    }
    if (trigger) trigger.setAttribute("aria-busy", "true");
    return load()
      .then(whenReady)
      .then(function () {
        document.body.classList.add("ks-flow-open");
        overlay.setAttribute("aria-hidden", "false");
        requestAnimationFrame(function () {
          overlay.classList.add("is-open");
          toFrame({ type: "keysleeves-matcher-open", context: pageContext });
          closeButton.focus({ preventScroll: true });
        });
      })
      .catch(function () { /* flow unavailable — leave the page untouched */ })
      .then(function () { if (trigger) trigger.removeAttribute("aria-busy"); });
  }

  function close() {
    if (!overlay.classList.contains("is-open")) return;
    overlay.classList.remove("is-open");
    overlay.setAttribute("aria-hidden", "true");
    document.body.classList.remove("ks-flow-open");
    if (lastTrigger && lastTrigger.focus) lastTrigger.focus({ preventScroll: true });
  }

  closeButton.addEventListener("click", close);
  overlay.addEventListener("click", function (event) {
    if (event.target === overlay) close();
  });
  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") close();
  });
  document.addEventListener("click", function (event) {
    var trigger = event.target.closest && event.target.closest(TRIGGER_SEL);
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });

  /* ------------------------------------------------------------- networking */

  function sessionId() {
    try {
      var current = window.localStorage.getItem(SESSION_KEY);
      if (current) return current;
      var next = "ks-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.localStorage.setItem(SESSION_KEY, next);
      return next;
    } catch (_) {
      return "";
    }
  }

  function dataUrlToBlob(value) {
    var parts = String(value).split(",");
    var mime = (parts[0].match(/data:([^;]+)/) || [])[1] || "image/jpeg";
    var binary = atob(parts[1] || "");
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  function asBlob(image) {
    if (!image) return null;
    if (typeof image === "string") {
      return image.indexOf("data:") === 0 ? dataUrlToBlob(image) : null;
    }
    return image; // Blob / File survive structured clone
  }

  function runMatch(request) {
    var blob = asBlob(request.image);
    if (!blob) {
      toFrame({ type: "ks-flow-match-response", requestId: request.requestId, ok: false, payload: { error: "no-image" } });
      return;
    }
    var form = new FormData();
    var name = blob.name || "key-photo.jpg";
    form.append("file", blob, name);
    /* The matcher rejects anything without an explicit consent=true. */
    form.set("consent", request.consent === true ? "true" : "false");
    form.set("promotionConsent", request.promotionConsent ? "true" : "false");
    form.set("contactConsent", request.contactConsent ? "true" : "false");
    var brand = request.brand || BRAND;
    if (brand) form.set("brand", brand);
    var sid = sessionId();
    if (sid) form.set("sessionId", sid);

    fetch(PROXY_ROOT + "/match", { method: "POST", body: form })
      .then(function (response) {
        return response
          .json()
          .catch(function () { return { error: "Unexpected response from the matcher." }; })
          .then(function (payload) { return { ok: response.ok, payload: payload }; });
      })
      .then(function (result) {
        toFrame({
          type: "ks-flow-match-response",
          requestId: request.requestId,
          ok: result.ok && !(result.payload && result.payload.error),
          payload: result.payload
        });
      })
      .catch(function () {
        toFrame({
          type: "ks-flow-match-response",
          requestId: request.requestId,
          ok: false,
          payload: { error: "network" }
        });
      });
  }

  function sendFeedback(body) {
    if (!body || !body.attemptId) return;
    var payload = {};
    Object.keys(body).forEach(function (key) { payload[key] = body[key]; });
    var sid = sessionId();
    if (sid && !payload.sessionId) payload.sessionId = sid;
    try {
      fetch(PROXY_ROOT + "/feedback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        keepalive: true,
        body: JSON.stringify(payload)
      }).catch(function () {});
    } catch (_) { /* fire and forget */ }
  }

  /* --------------------------------------------------------------- messages */

  window.addEventListener("message", function (event) {
    if (event.source !== frame.contentWindow) return;
    var data = event.data;
    if (!data || !data.type) return;

    switch (data.type) {
      case "keysleeves-matcher-ready":
        frameReady = true;
        overlay.classList.add("is-frame-ready");
        overlay.dispatchEvent(new Event("ks-flow-ready"));
        if (overlay.classList.contains("is-open")) toFrame({ type: "keysleeves-matcher-open", context: pageContext });
        break;
      case "keysleeves-matcher-close":
        close();
        break;
      case "ks-flow-match-request":
        runMatch(data);
        break;
      case "ks-flow-feedback":
        sendFeedback(data.body);
        break;
      case "ks-flow-attribution":
        handleAttribution(data.body).then(function (ok) {
          toFrame({ type: "ks-flow-attribution-response", requestId: data.requestId, ok: !!ok });
        });
        break;
      default:
        break;
    }
  });

  window.KsFlow = { open: open, close: close, overlay: overlay, frame: frame, proxyRoot: PROXY_ROOT };
})();
